import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang, api } from "../store";
import { PageHead, Reveal } from "../components/Reveal";
import { RequestForm } from "../components/RequestForm";
import Footer from "../components/Footer";

function EquipmentCatalog() {
  const { lang, t } = useLang();
  const [catalog, setCatalog] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/catalog?type=attrezzatura").then((r) => setCatalog(r.data)).catch(() => setCatalog([]));
  }, []);

  const cats = ["all", ...new Set(catalog.map((i) => i.category))];
  const filtered = cat === "all" ? catalog : catalog.filter((i) => i.category === cat);

  return (
    <>
      <div className="mb-10 flex flex-wrap gap-2 border-y border-white/10 py-5" data-testid="catalog-categories">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`equip-cat-${c}`}
            className={`font-mono2 rounded-full border px-4 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${cat === c ? "border-amber-500 bg-amber-600/20 text-amber-400" : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"}`}
          >
            {c === "all" ? t("rental.allCats") : t(`rental.cats.${c}`)}
          </button>
        ))}
      </div>

      <p
        className="mb-10 flex flex-wrap items-baseline gap-x-2 border-l-2 border-amber-500 bg-[#120e07] py-4 pl-5 pr-5 text-sm leading-relaxed text-slate-300"
        data-testid="rental-pro-gear-note"
      >
        {t("rental.proGear")}
        <Link to="/contatti" className="font-mono2 text-[11px] uppercase tracking-widest text-amber-500 underline-offset-4 hover:underline" data-testid="rental-pro-gear-link">
          {t("nav.contatti")} →
        </Link>
      </p>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
        {filtered.map((item, i) => (
          <Reveal key={item.id} delay={(i % 3) * 0.06}>
            <article className="dc-card group flex h-full flex-col overflow-hidden rounded" data-testid={`catalog-item-${item.id}`}>
              <div className="poster-frame relative aspect-[16/11] overflow-hidden bg-[#0f1216]">
                <img src={item.image} alt={item.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                <span className="font-mono2 absolute right-3 top-3 rounded bg-black/85 px-2.5 py-1 text-xs tracking-widest text-amber-500" data-testid={`catalog-rate-${item.id}`}>
                  €{item.rate}{t("rental.perDay")}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-lg uppercase tracking-wide">{item.name}</h3>
                {item.subtitle && <p className="font-mono2 mt-1 text-[10px] tracking-[0.2em] text-amber-600/80">{item.subtitle}</p>}
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{lang === "it" ? item.desc_it : item.desc_en}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {item.specs.map((s) => (
                    <span key={s} className="font-mono2 rounded border border-white/10 px-2 py-0.5 text-[10px] tracking-wider text-slate-500">{s}</span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <p className="font-mono2 mt-10 rounded border border-amber-600/25 bg-[#141009] p-5 text-xs leading-relaxed tracking-wide text-slate-400" data-testid="rental-deposit-note">
        {t("rental.deposit")}
      </p>
    </>
  );
}

export default function Rental({ type }) {
  const { t } = useLang();
  const isEquip = type === "attrezzatura";

  return (
    <div className="min-h-screen" data-testid={`rental-${type}-page`}>
      <PageHead
        kicker={isEquip ? "RENTAL · CAMERA DEPT" : "RENTAL · WARDROBE"}
        title={isEquip ? t("rental.titleEquip") : t("rental.titleCost")}
        sub={isEquip ? t("rental.subEquip") : ""}
      />

      <div className="mx-auto max-w-[1600px] px-6 pb-24">
        {isEquip ? (
          <EquipmentCatalog />
        ) : (
          <Reveal>
            <p className="font-serif-it max-w-3xl text-xl italic leading-relaxed text-slate-300 sm:text-2xl" data-testid="costumes-intro">
              {t("rental.costumesText")}
            </p>
          </Reveal>
        )}

        <div className="mt-14 max-w-3xl" id="richiesta">
          <Reveal>
            <RequestForm
              subject={isEquip ? "Noleggio attrezzatura" : "Noleggio costumi e oggetti di scena"}
              testid={isEquip ? "equipment-request-form" : "costumes-request-form"}
            />
          </Reveal>
        </div>
      </div>
      <Footer />
    </div>
  );
}
